import type { CaseStage, IssueCase, IssueDetail } from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { ExternalLink, Link2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIssueMgmtWrite } from '../../hooks/useIssueMgmt.js';
import { useBacklogSync, useIssueCases } from '../../hooks/useIssueTodo.js';
import { backlogUrl } from '../../lib/backlog-url.js';
import {
  formatIssueTime,
  formatShortDate,
  ISSUE_CASE_STATE_LABEL_KEYS,
} from '../../lib/issue-mgmt-format.js';
import { CASE_ATTENTION_REASON_KEYS } from '../../lib/issue-mgmt-next-action.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 案件欄を畳まずに出す件数（超えたら「ほかN件」で開く）。
 * @graph-connects none
 */
export const CASE_FOLD_AFTER = 5;

/**
 * 案件の進み（Backlog の状態の大分類）の札の色。
 * @graph-connects none
 */
const STAGE_VARIANTS: Record<CaseStage, 'default' | 'info' | 'success'> = {
  drafted: 'default',
  in_progress: 'info',
  done: 'success',
};

/**
 * 案件1行の札（Backlog の状態名。写しがまだ無ければ確認待ち、見つからなければ見つかりません）。
 * @graph-connects none
 */
function CaseBadge({ row }: { row: IssueCase }) {
  const { t } = useTranslation();
  if (row.deleted) return <Badge variant="warning">{t('issueMgmt.cases.deleted')}</Badge>;
  if (row.state !== 'confirmed') {
    return (
      <Badge variant={row.state === 'pending' ? 'default' : 'warning'}>
        {t(ISSUE_CASE_STATE_LABEL_KEYS[row.state])}
      </Badge>
    );
  }
  return (
    <Badge variant={row.stage === null ? 'default' : STAGE_VARIANTS[row.stage]}>
      {row.statusName ?? t('issueMgmt.common.dash')}
    </Badge>
  );
}

/**
 * 課題に紐づく Backlog の案件欄（PR-04/05 の画面）。案件ごとに Backlog の写し（題名・状態・担当・期限・リリース日）を出し、
 * 確かめてもらう理由がある案件には理由の1文と「Backlogで確かめた」を出す。編集キーの人は案件を紐づける・外すができる。
 * 下に最終同期の時刻を添え、6件以上は畳む。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題に紐づく Backlog 案件の状態・担当・期限・リリース日と完了の件数を見せ、見つからない・保留中・期限超過の案件をBacklogで確かめた印で片づけ、案件を紐づける・外す
 * @graph-connects useIssueCases [calls] 案件欄
 * @graph-connects useBacklogSync [calls] 最終同期
 * @graph-connects useIssueMgmtWrite [calls] checkCase・unlinkCase
 * @graph-connects backlogUrl [calls] Backlog へのリンク
 */
export function IssueCases({
  issue,
  canEdit,
  onLinkCase,
  now,
}: {
  issue: IssueDetail;
  canEdit: boolean;
  onLinkCase: () => void;
  now: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const cases = useIssueCases(issue.id);
  const sync = useBacklogSync();
  const checkCase = useIssueMgmtWrite('checkCase');
  const unlinkCase = useIssueMgmtWrite('unlinkCase');
  const [unfolded, setUnfolded] = useState(false);
  const data = cases.data;
  const rows = data?.cases ?? [];
  const shown = unfolded ? rows : rows.slice(0, CASE_FOLD_AFTER);
  const canLink = canEdit && issue.verdict === 'accepted' && issue.devRequired !== false;
  const busy = checkCase.isPending || unlinkCase.isPending;
  const lastSynced = sync.data?.lastSyncedAt ?? null;
  const dash = t('issueMgmt.common.dash');

  const summary = (() => {
    if (data === undefined) return t('issueMgmt.common.loading');
    if (data.total === 0) return t('issueMgmt.cases.none');
    return t('issueMgmt.cases.progress', {
      total: String(data.total),
      completed: String(data.completed),
    });
  })();

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] px-4 py-3">
        <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('issueMgmt.cases.heading')}
        </h2>
        <span className="text-xs text-[var(--color-text-muted)]">{summary}</span>
        {canLink && (
          <Button variant="secondary" size="sm" className="ml-auto" onClick={onLinkCase}>
            <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t('issueMgmt.next.buttonLinkCase')}
          </Button>
        )}
      </div>
      {cases.isError ? (
        <p className="px-4 py-3 text-sm text-[var(--color-error)]">
          {t('issueMgmt.common.loadError')}
        </p>
      ) : data !== undefined && rows.length === 0 ? (
        <p className="px-4 py-3 text-sm text-[var(--color-text-muted)]">
          {t('issueMgmt.cases.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-[var(--color-border)]">
          {shown.map((row) => (
            <li key={row.caseKey} className="space-y-1 px-4 py-2.5 text-sm">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <a
                  href={backlogUrl(row.caseKey)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-40 shrink-0 items-center gap-1 font-medium text-[var(--color-accent-primary)] hover:underline"
                >
                  {row.caseKey}
                  <ExternalLink className="h-3 w-3" aria-hidden="true" />
                </a>
                <span className="min-w-[10rem] flex-1 text-[var(--color-text-primary)]">
                  {row.title ?? dash}
                </span>
                <CaseBadge row={row} />
                <span className="text-xs text-[var(--color-text-muted)]">
                  {t('issueMgmt.cases.meta', {
                    assignee: row.assigneeName ?? dash,
                    due: row.dueDate === null ? dash : formatShortDate(row.dueDate, now),
                  })}
                  {row.releasedDate !== null &&
                    `・${t('issueMgmt.card.releasedOn', { date: formatShortDate(row.releasedDate, now) })}`}
                </span>
                {canEdit && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      unlinkCase.mutate(
                        { issueId: issue.id, caseKey: row.caseKey },
                        {
                          onSuccess: (result) => {
                            toast.notifySaved(
                              t('issueMgmt.cases.unlinked', {
                                title: issue.title,
                                key: row.caseKey,
                              }),
                              { operationId: result.operationId },
                            );
                          },
                          onError: toast.notifyError,
                        },
                      );
                    }}
                    className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:underline disabled:opacity-60"
                  >
                    {t('issueMgmt.cases.unlink')}
                  </button>
                )}
              </div>
              {row.attention !== null && !row.checked && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-[color-mix(in_srgb,var(--color-warning)_12%,transparent)] px-3 py-1.5 text-xs text-[var(--color-text-primary)]">
                  <span className="min-w-0 flex-1">
                    {t(CASE_ATTENTION_REASON_KEYS[row.attention], {
                      status: row.statusName ?? dash,
                      date: row.dueDate === null ? dash : formatShortDate(row.dueDate, now),
                    })}
                  </span>
                  {canEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        checkCase.mutate(
                          { issueId: issue.id, caseKey: row.caseKey },
                          {
                            onSuccess: (result) => {
                              toast.notifySaved(
                                t('issueMgmt.card.checkedSaved', { title: issue.title }),
                                {
                                  operationId: result.operationId,
                                  detail: result.recorded
                                    ? t('issueMgmt.card.checkedDetail')
                                    : t('issueMgmt.card.checkedRetry'),
                                },
                              );
                            },
                            onError: toast.notifyError,
                          },
                        );
                      }}
                    >
                      {t('issueMgmt.next.buttonChecked')}
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
          {!unfolded && rows.length > CASE_FOLD_AFTER && (
            <li>
              <button
                type="button"
                onClick={() => {
                  setUnfolded(true);
                }}
                className="w-full px-4 py-2.5 text-left text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]"
              >
                {t('issueMgmt.cases.more', { n: String(rows.length - CASE_FOLD_AFTER) })}
              </button>
            </li>
          )}
        </ul>
      )}
      <p className="border-t border-[var(--color-border)] px-4 py-2 text-xs text-[var(--color-text-muted)]">
        {lastSynced === null
          ? t('issueMgmt.cases.neverSynced')
          : t('issueMgmt.cases.lastSynced', {
              at: formatIssueTime(lastSynced, null, now) ?? dash,
            })}
      </p>
    </section>
  );
}
